// @ts-ignore esModuleInterop
import React from 'react'
import moment from 'moment-timezone'
// @ts-ignore no types
import PropTypes from 'prop-types'
// @ts-ignore no types
import _ from 'lodash'
import _cx from 'classnames/bind'
// @ts-ignore no types
import numeral from 'numeral'
import * as graph from '@adc/sdk'
import {
  Icon,
  Text,
  constants,
  ListingReserve,
  ScrollSpy,
  // @ts-ignore no types
} from '@adc/ui-adc'
import type { AuctionBlueprintOnlineReactiveArgs } from '@adc/blueprint'
import * as blueprint from '@adc/blueprint'
import { useListingContext } from '../../context/listing'
import analytics from '../../services/analytics'
import PopupTooltip from '../../components/popupTooltip'
import { getMarketingAttribute } from '../../utils/graph'
import { formatMoneyWithCentsExceptZero } from '../../utils/formatters'
import ERVLowerModal from '../../components/ervLowerModal'
import {
  NOTIFY_DEFAULT_TEXT,
  NOTIFY_SUBMITTED_TEXT,
  NOTIFY_RESERVE,
  EST_DEBT_TOOLTIP_CONTENT,
  RENTAL_ESTIMATE_DESCRIPTION,
  ARV_TOOLTIP_CONTENT,
  ERV_LOWER_RESERVE,
  ERV_LOWER_CREDIT_BID,
} from '../../statics'
import { getListPrice } from '../../utils/listing'
// @ts-ignore no types
import cls from './index.scss'
import { isAuctionSold } from '../auctionDetails/utils'

const cx = _cx.bind(cls)

/**
 * NOTE: We have other reserve tooltips elsewhere, notably in the bid banner. They are slightly different to the
 * this which is why they aren't used here. From inspecting history that looks intentional to makie that message more
 * contextual for when the auction is ongoing.
 */
const RESERVE_TOOLTIP_CONTENT = (
  <div>
    <p>This is the lowest acceptable price by the seller.</p>
    <p>
      Sellers don't always disclose the Reserve, but if they do, we can notify
      you
    </p>
  </div>
)

const EstCreditBidTooltipContent = ({
  amountShown,
}: {
  amountShown: boolean
}) => (
  <div>
    <p>This is the estimated amount the lender will bid at the sale.</p>
    {amountShown ? (
      <p>
        Buyers will have to bid above this amount to purchase at auction. Amount
        subject to change.
      </p>
    ) : (
      <p>
        Lenders don't always disclose a Credit Bid, but if they do, it's usually
        shared just before the sale.
      </p>
    )}
  </div>
)

/**
 * This value seems to have been copy/pasted in lots of places in this app. I have extracted here so we can do exact
 * matches without copy/pasting strings throughout the file.
 *
 * TODO: Extract this into constant and replace hard coded instances of this string that are present for the same
 * purpose.
 */
const NULL_VALUE = 'TBD'

numeral.defaultFormat('$0,0')
numeral.nullFormat(NULL_VALUE)

const { marketingAttributeTypes } = constants.marketingAttributeTypes
const defaultAttributeMap = marketingAttributeTypes.reduce(
  (prev: any, { text, value }: { text: any; value: any }) => {
    prev[value] = text
    return prev
  },
  {}
)
interface ValuationRowProps {
  elmId: string
  firstCol: any
  secondCol: any
  annotation?: any
}
export const ValuationRow = ({
  elmId,
  firstCol,
  secondCol,
  annotation,
}: ValuationRowProps) => (
  <div className={cx('valuation-row')} data-elm-id={`${elmId}_row`}>
    <div className={cx('row')}>
      <div className={cx('label')}>
        <Text as='span' color='gray' data-elm-id={`${elmId}_label`}>
          {firstCol}
        </Text>
      </div>
      <div className={cx('value')}>
        <Text color='gray' weight='heavy' data-elm-id={`${elmId}_value`}>
          {secondCol}
        </Text>
      </div>
      {annotation && <div className={cx('annotation')}>{annotation}</div>}
    </div>
  </div>
)

interface MarketingAttributeProps {
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  attributeType: string
  value: any
  tooltip?: any
  format: any
  elmId: string
}

const MarketingAttribute = ({
  listing,
  attributeType,
  value,
  tooltip,
  format = _.identity,
  elmId = 'marketing_attribute',
}: MarketingAttributeProps) => {
  const attr = getMarketingAttribute(listing, attributeType)

  if (!attr.show || !(value || attr.default_display_value !== 'HIDDEN'))
    return null
  const defaultLabel = defaultAttributeMap[attr.attribute_type]
  const label = (
    <span className='u-pr-2'>
      {attr.label || defaultLabel}
      {tooltip && (
        <PopupTooltip
          content={tooltip}
          elmId={`${elmId}_popup_tooltip`}
          position='top center'
          trigger={<Icon name='info circle' data-elm-id={`${elmId}_popup`} />}
        />
      )}
    </span>
  )
  return (
    <ValuationRow
      elmId={elmId}
      firstCol={label}
      secondCol={value ? format(value) : attr.default_display_value}
    />
  )
}

const isReserveDisplayed = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
) => _.get(listing, 'listing_configuration.is_reserve_displayed', false)

interface ReserveInfoProps {
  elmId: string
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  notifyMeTrigger: React.ReactNode
}

const ReserveInfo = ({ elmId, listing, notifyMeTrigger }: ReserveInfoProps) => {
  // eslint disables because ui-adc not typed yet
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
  const [reserveLabel, value] = ListingReserve.rawTuple?.(
    listing,
    NULL_VALUE
  ) ?? [NULL_VALUE, NULL_VALUE]

  if (!isReserveDisplayed(listing)) return null

  const undisclosedReserve = value === NULL_VALUE

  const tooltip = listing.event?.trustee_sale ? (
    <EstCreditBidTooltipContent amountShown={!undisclosedReserve} />
  ) : (
    RESERVE_TOOLTIP_CONTENT
  )

  // NOTE: Slightly confusing down below but even though this is the same value, for REO we won't show the tooltip if
  // there is no value (I don't know why since it would still be explaining what the row is for) but trustee will
  // always show a tooltip but change language inside depending on whether the reserve is on the page or not.
  const label = (
    <span className='u-pr-2'>
      {reserveLabel}{' '}
      {(listing.event?.trustee_sale || undisclosedReserve) && (
        <PopupTooltip
          content={
            <section data-elm-id='reserve_not_disclosed_popup_tooltip'>
              {tooltip}
            </section>
          }
          elmId={`${elmId}_popup_tooltip`}
          position='top center'
          trigger={<Icon name='info circle' data-elm-id={`${elmId}_popup`} />}
        />
      )}
    </span>
  )

  const rowValue = undisclosedReserve
    ? listing.listing_configuration?.is_reserve_displayed && notifyMeTrigger
    : value

  return <ValuationRow elmId='reserve' firstCol={label} secondCol={rowValue} />
}

interface ValuationDetailsProps {
  toggleSaveProperty?: any
  realtyTrackReport?: any
  showRentalEstimate?: boolean
  featureFlags?: any
  isPropertyReportAvailable?: boolean
}
const AfterRepairValue = ({
  isPropertyReportAvailable,
}: {
  isPropertyReportAvailable: boolean
}) => {
  const { listing } = useListingContext()
  const arv = listing?.primary_property?.summary?.valuation
  const arvLabel =
    typeof arv !== 'number' || isNaN(arv)
      ? 'Not Available'
      : numeral(arv).format()

  const shouldShowReserveOrCreditBidAmount = Boolean(
    listing.listing_configuration?.is_reserve_displayed
  )

  const shouldShowAnnotation =
    !isAuctionSold(listing) && // was bStatus.isAuctionSold
    shouldShowReserveOrCreditBidAmount &&
    arv &&
    listing.reserve_price &&
    arv < listing.reserve_price // was bStatus.estCreditBid || bStatus.bidValuationInformation.reserve_amount

  return listing.__methods__.isSparkOffer() ? null : (
    <ValuationRow
      elmId='arv'
      firstCol={
        <span>
          Est. Resale{' '}
          <span className='u-nowrap'>
            Value{' '}
            <PopupTooltip
              content={ARV_TOOLTIP_CONTENT}
              elmId='arv_popup_tooltip'
              position='top center'
              trigger={<Icon name='info circle' data-elm-id='arv_popup' />}
            />
          </span>
        </span>
      }
      secondCol={arvLabel}
      annotation={
        <div>
          {isPropertyReportAvailable && arv && !isNaN(arv) && (
            <div className={cx('u-display-f', 'u-display-f-between')}>
              <a href='#comp_sales'>See Comparable Sales</a>
              <Text
                className={cx('ca-source', 'u-mb-1')}
                color='gray'
                size='mini'
              >
                Source: Collateral Analytics
              </Text>
            </div>
          )}
          {shouldShowAnnotation && (
            <ERVLowerModal
              type={listing.auction?.is_online ? 'reo' : 'tps'}
              trigger={
                <Text size='tiny' className='u-pb-1'>
                  <a data-elm-id='erv_lower_reserve_modal_trigger'>
                    {listing.auction?.is_online
                      ? ERV_LOWER_RESERVE
                      : ERV_LOWER_CREDIT_BID}
                  </a>
                </Text>
              }
            />
          )}
        </div>
      }
    />
  )
}

const notifyMe = (toggleSaveProperty: any) => {
  toggleSaveProperty({
    source: NOTIFY_RESERVE,
  })
}

const notifyMeButton = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing'],
  toggleSaveProperty: any
) => {
  return listing?.primary_property?.is_currently_saved ? (
    <Text color='gray' size='tiny' className={cx('notify-submitted-text')}>
      {NOTIFY_SUBMITTED_TEXT}
    </Text>
  ) : (
    <a
      onClick={() => notifyMe(toggleSaveProperty)}
      data-elm-id='notify_me_link'
    >
      {NOTIFY_DEFAULT_TEXT}
    </a>
  )
}
const openingBidFormatter = (val: number) => {
  const { listing } = useListingContext()
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return listing.listing_configuration?.asset_type ===
    graph.enums.AssetType.Foreclosure
    ? formatMoneyWithCentsExceptZero(val, 'TBD')
    : numeral(val).format('$0,0', Math.floor)
}

const renderAuctionEndDate = () => {
  const { segment, listing } = useListingContext()
  return (
    segment?.segment_status === graph.enums.OnlineAuctionSegmentStatus.Ended &&
    segment?.subject_to_status === graph.enums.SubjectToStatus.Pending &&
    listing.listing_status !== graph.enums.ListingStatus.Closed && ( // bStatus.isSoldPending
      <div data-elm-id='auction_end_date_root'>
        <ValuationRow
          elmId='auction_end_date'
          firstCol='Auction Ended'
          secondCol={moment(segment.initial_end_date).format('MM/DD/YYYY')} // bStatus.bidValuationInformation.formatted_end_date, was formatted like format('MM/DD/YYYY')
        />
      </div>
    )
  )
}

const renderStateDeposit = (
  segment: blueprint.AuctionBlueprintLiveReactiveArgs['segment'],
  listing: blueprint.AuctionBlueprintLiveReactiveArgs['listing']
) => {
  if (!segment?.__methods__?.getStateDepositValues) return null
  const { stateDepositRule, stateDepositLabel, shouldShowStateDeposit } =
    segment.__methods__.getStateDepositValues()

  if (!shouldShowStateDeposit) return null

  const propertyStateLowercaseAbbreviation = _.get(
    listing,
    'primary_property.summary.address.country_primary_subdivision',
    ''
  ).toLowerCase()

  const trackPopupOpened = () => {
    analytics.popup(
      `${propertyStateLowercaseAbbreviation}_winning_bid_deposit`,
      {
        event: 'hover-event',
      }
    )
  }

  const firstColumn = (
    <span className='u-pr-2'>
      Winning Bid Deposit{' '}
      <PopupTooltip
        content={
          <ScrollSpy selector='#prepare_your_finances'>
            {({ scrollTo }: { scrollTo: any }) => (
              <>
                A Winning Bid Deposit is required for each property you
                successfully bid on and must be paid upon being declared the
                winning bidder. Further action will be required to submit the
                remaining balance.{' '}
                <a
                  onClick={scrollTo}
                  data-elm-id='prepare_for_auction_tooltip-content'
                  className={cx('lighter-link-tooltip')}
                >
                  Learn more
                </a>
              </>
            )}
          </ScrollSpy>
        }
        elmId='state_deposit_popup_tooltip'
        position='top center'
        trigger={<Icon name='info circle' data-elm-id='state_deposit_popup' />}
        onOpen={trackPopupOpened}
      />
    </span>
  )
  return (
    stateDepositRule && (
      <div data-elm-id='state_deposit_root' className={cx('state-deposit-row')}>
        <ValuationRow
          elmId='state_deposit'
          firstCol={firstColumn}
          secondCol={stateDepositLabel}
        />
      </div>
    )
  )
}
const numeralFormat = (val: number) => numeral(val).format()

const renderHotIcon = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
) => {
  return (
    listing && listing?.is_hot && <span className={cx('hot-icon')}>HOT</span>
  )
}
const renderPotentialReturnMessage = (
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
) => {
  return (
    listing &&
    listing.is_hot && (
      <Text color='gray' size='tiny' className='u-mt-3'>
        This investment property may have a higher potential return compared to
        similar properties.{' '}
        <a target='_blank' rel='noreferrer' href='/lp/learn-more/glossary/#h'>
          Find out why in the Help Center.
        </a>
      </Text>
    )
  )
}
const renderMarketingAttributes = (toggleSaveProperty: any) => {
  const { listing, segment } = useListingContext()
  // was:
  // !bStatus.isAuctionSold &&
  // (bStatus.shouldShowAuctionReserveAmount ||
  //   bStatus.shouldShowAuctionReserveNotifyMe)
  const showReserveRow =
    !isAuctionSold(listing) &&
    listing.listing_configuration?.is_reserve_displayed

  const showListPrice = listing.__methods__.shouldDisplayListPrice()
  const showOpeningBidRow =
    !showListPrice && listing.listing_summary?.show_opening_bid // listing.listing_summary?.show_opening_bid replaced bStatus.showOpeningBid
  const estDebt = parseInt(
    // @ts-ignore TODO:
    _.get(listing, 'auction.bid_instruction.nos_amount', 0),
    10
  )

  const showEstDebtRow = Number.isFinite(estDebt)

  return (
    <>
      {showReserveRow && (
        <ReserveInfo
          elmId='reserve'
          listing={listing}
          notifyMeTrigger={notifyMeButton(listing, toggleSaveProperty)}
        />
      )}
      {showListPrice && (
        <ValuationRow
          elmId='list_price'
          firstCol='List Price'
          secondCol={getListPrice(listing)}
        />
      )}
      {showOpeningBidRow && (
        <MarketingAttribute
          elmId='opening_bid'
          listing={listing}
          attributeType='OPENING_BID'
          value={
            segment?.starting_bid_amount ||
            _.get(listing, 'auction.starting_bid', 0)
          }
          format={openingBidFormatter}
        />
      )}
      {showEstDebtRow && (
        <MarketingAttribute
          elmId='estimated_debt'
          listing={listing}
          attributeType='NOS_AMOUNT'
          tooltip={EST_DEBT_TOOLTIP_CONTENT}
          value={estDebt}
          format={numeralFormat}
        />
      )}
      <MarketingAttribute
        elmId='original_balance'
        listing={listing}
        attributeType='ORIGINAL_PRINCIPAL_BALANCE'
        tooltip={EST_DEBT_TOOLTIP_CONTENT}
        value={_.get(
          listing,
          'listing_configuration.principal_original_amount',
          0
        )}
        format={numeralFormat}
      />
    </>
  )
}

const renderRentalEstimate = (realtyTrackReport: any) => {
  const value = _.get(realtyTrackReport, 'rentalRange.estimated')
  const elmId = 'rental_estimate_row'
  const firstCol = (
    <div className={cx('rental-estimate')}>
      <span>
        Rental Estimate{' '}
        <PopupTooltip
          content={RENTAL_ESTIMATE_DESCRIPTION}
          position='top center'
          elmId={`${elmId}_popup_tooltip`}
          trigger={
            <Icon name='info circle' data-elm-id={`${elmId}_popup_trigger`} />
          }
        />
      </span>
    </div>
  )

  const annotation = (
    <div className={cx('u-display-f', 'u-display-f-between')}>
      <a href='#comp_sales'>See Details</a>
      <Text className={cx('ca-source', 'u-mb-1')} color='gray' size='mini'>
        Source: Collateral Analytics
      </Text>
    </div>
  )

  return (
    <ValuationRow
      elmId={elmId}
      firstCol={firstCol}
      secondCol={numeral(value).format()}
      annotation={Boolean(+value) && annotation}
    />
  )
}
const ValuationDetails = (props: ValuationDetailsProps) => {
  const {
    toggleSaveProperty,
    realtyTrackReport,
    showRentalEstimate,
    featureFlags,
    isPropertyReportAvailable,
  } = props

  const { segment, listing } = useListingContext()
  // NOTE: we're making an exception for this casting due to these components being specifically for live auctions
  const liveSegment =
    segment as unknown as blueprint.AuctionBlueprintLiveReactiveArgs['segment']
  return (
    <div className={cx('investment-info')}>
      <Text.Header
        as='h2'
        level={1}
        compact
        // @ts-ignore fix types in DS, export enum if possible
        size='huge'
        weight='medium'
        color='gray'
      >
        {renderHotIcon(listing)} Price Insights
      </Text.Header>
      {renderPotentialReturnMessage(listing)}
      <div>
        <AfterRepairValue
          isPropertyReportAvailable={!!isPropertyReportAvailable}
        />
        {!listing.listing_configuration?.is_direct_offer_enabled &&
          renderMarketingAttributes(toggleSaveProperty)}
        {showRentalEstimate && renderRentalEstimate(realtyTrackReport)}
        {!listing.listing_configuration?.is_direct_offer_enabled &&
          renderAuctionEndDate()}
        {featureFlags['fe.resi.enable_state_deposit_treatment'] &&
          !listing.auction?.is_online &&
          segment?.__methods__ &&
          renderStateDeposit(liveSegment, listing)}
      </div>
    </div>
  )
}

export default ValuationDetails
